import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Store, HardHat, Factory, ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { IndustryIcon } from "../components/icons";
import { ADVANTAGES } from "../data/content";

const pillarIcons = [Store, HardHat, Factory];

export default function About() {
  const { tr, lang, isAr } = useLang();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const Arrow = isAr ? ArrowUpRight : ArrowRight;

  return (
    <div data-testid="about-page" className="pt-28 sm:pt-36">
      <SEO page="about" lang={lang} />

      {/* Intro */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline={tr.about.badge} title={tr.about.title} />
        <Reveal variant="up" delay={0.1}>
          <p className="mt-8 max-w-4xl text-xl sm:text-2xl font-300 leading-snug text-ink">{tr.about.lead}</p>
        </Reveal>
      </section>

      {/* Showroom — standout parallax */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal variant="scale">
          <div ref={imgRef} className="relative overflow-hidden rounded-[2.5rem] border border-white/60 shadow-2xl shadow-brand-blue/15">
            <div className="h-[360px] sm:h-[560px] overflow-hidden">
              <motion.img
                src="/assets/showroom.jpeg"
                alt="ICON Trading Co. showroom storefront in Dammam"
                style={{ y }}
                className="h-[140%] w-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-6 sm:flex-row sm:items-end sm:justify-between sm:p-10">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-600 tracking-wide text-white backdrop-blur">
                  EST. 2024
                </span>
                <p className="mt-3 text-lg sm:text-2xl font-700 text-white">{tr.about.showroomCaption}</p>
              </div>
              <Stagger className="flex gap-6 text-white" stagger={0.1}>
                {[{ k: "Strategic", v: "Showroom" }, { k: "Local", v: "Warehouse" }, { k: "B2B & Direct", v: "Service" }].map((c) => (
                  <StaggerItem key={c.v}>
                    <p className="text-xs uppercase tracking-wide text-white/60">{c.k}</p>
                    <p className="text-sm sm:text-base font-700">{c.v}</p>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Body text — two columns */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 sm:pb-28">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal variant="left">
            <p className="text-base sm:text-lg leading-relaxed text-slate">{tr.about.body1}</p>
          </Reveal>
          <Reveal variant="right">
            <p className="text-base sm:text-lg leading-relaxed text-slate">{tr.about.body2}</p>
          </Reveal>
        </div>
      </section>

      {/* How we serve */}
      <section className="bg-surface py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline={tr.about.badge} title={tr.about.pillarsTitle} />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-3" stagger={0.12}>
            {tr.about.pillars.map((p, i) => {
              const Icon = pillarIcons[i];
              return (
                <StaggerItem key={i} variant="up">
                  <div className="group h-full rounded-3xl border border-border bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-blue hover:shadow-xl hover:shadow-brand-blue/10">
                    <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-blue/10 text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </span>
                    <h3 className="mt-6 text-xl font-700 text-ink">{p.t}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate">{p.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* Advantages */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading overline={tr.vision.advTitle} title={tr.about.advTitle} center />
        <Stagger className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4" stagger={0.05}>
          {ADVANTAGES.map((a) => (
            <StaggerItem key={a.icon} variant="fade">
              <div className="group h-full bg-white p-7 transition-colors hover:bg-surface">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-red/10 text-brand-red transition-transform group-hover:scale-110">
                  <IndustryIcon name={a.icon} className="h-5 w-5" />
                </span>
                <h4 className="mt-5 text-base font-700 text-ink">{a[lang].t}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate">{a[lang].d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal variant="up" className="mt-14 flex justify-center">
          <Link to="/products" className="group inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-600 text-white transition-all hover:bg-brand-blue-dark">
            {tr.homeCaps.cta}
            <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
